import {Box} from '@mantine/core';
import React, {useMemo} from 'react';
import {debounce} from 'ts-debounce';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {useAutoUpdatePreview} from 'analysis-tool/data-frame-creation/hooks/useAutoUpdatePreview';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ScratchFileContent} from 'pipelines/files/ScratchFileTypes';
import {PreviewChart, PreviewOutput, PreviewStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getOutputStepDataShape} from 'pipelines/StepDataUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
  AsyncStatus,
  AsyncState,
} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import PresentationalStepDataPreview, {PreviewStepDatas, StepDataRole} from './PresentationalStepDataPreview';

const previewGlobalState = makeUseGlobalClientState<AsyncState<PreviewOutput>>(NOT_FETCHED_ASYNC_STATE);

export interface LiveStepDataPreviewProps {
  draftStep: PipelineStep;
  clientStateId: string;
}

interface LiveStepDataPreviewState {
  previewStepDatas: PreviewStepDatas | null;
  previewStatus: AsyncStatus;
  statusMessage: string | null;
  /** The `userMessage` from a failed preview response, if any. */
  errorUserMessage: string | null;
}

/**
 * Split a preview output's raw datas into the per-kind, step-data-keyed shape consumed by
 * `PresentationalStepDataPreview`, preserving output step data order.
 */
export function toPreviewStepDatas(
  previewOutput: PreviewOutput,
  stepType: PipelineStepType,
  stepVersion: number
): PreviewStepDatas {
  const stepDatas: PreviewStepDatas = {};
  Object.entries(previewOutput.outputStepDatas).forEach(([stepDataName, data]) => {
    const {kind} = getOutputStepDataShape(stepType, stepVersion, stepDataName);
    stepDatas[stepDataName] = {
      charts: kind === PipelineStepDataKind.CHART ? (data as Array<PreviewChart>) : [],
      dataFrames: kind === PipelineStepDataKind.DATASET ? (data as Array<DataFrame>) : [],
      files: kind === PipelineStepDataKind.FILE ? (data as Array<ScratchFileContent>) : [],
    };
  });
  return stepDatas;
}

function useLivePreviewData({
  draftStep,
  clientStateId,
}: {
  draftStep: PipelineStep;
  clientStateId: string;
}): LiveStepDataPreviewState {
  const [previewState, setPreviewState] = previewGlobalState.useGlobalClientState(clientStateId);

  const livePreviewRequestData = draftStep.config
    ? {
        stepType: draftStep.stepType,
        stepConfig: draftStep.config,
        stepInput: Object.fromEntries(
          draftStep.inputStepDatas.map((inputStepData) => [
            inputStepData.name,
            inputStepData.rawDatas.map(({id}) => id),
          ])
        ),
      }
    : null;

  const previewOutput = getMostRecentData(previewState);

  const previewStepDatas = previewOutput
    ? toPreviewStepDatas(previewOutput, draftStep.stepType, draftStep.stepVersion)
    : null;

  const debouncedGeneratePreview = useMemo(
    () =>
      debounce(
        (
          data: PreviewStepParams<PipelineStepType>,
          setAsyncState: (
            nextState: AsyncState<PreviewOutput<PipelineStepType>, [PreviewStepParams<PipelineStepType>]>
          ) => void
        ) => {
          AnalysisActions.runPreviewAnalysisStep(data, {status: AsyncStatus.NOT_FETCHED}, setAsyncState);
        },
        300
      ),
    []
  );

  useAutoUpdatePreview(
    livePreviewRequestData as PreviewStepParams<PipelineStepType> | null,
    debouncedGeneratePreview,
    setPreviewState
  );

  const errorResponse = previewState.status === AsyncStatus.ERROR ? previewState.responseJSON : null;
  return {
    previewStepDatas,
    previewStatus: previewState.status,
    statusMessage: (previewState.status === AsyncStatus.ERROR && previewState.message) || null,
    errorUserMessage: errorResponse?.userMessage ?? null,
  };
}

/**
 * Wrapper for `<PresentationalStepDataPreview>`, which generates live and auto-updating previews.
 *
 * @package
 */
export default function LiveStepDataPreview({
  draftStep,
  clientStateId,
}: LiveStepDataPreviewProps): JSX.Element {
  const {previewStepDatas, previewStatus, statusMessage, errorUserMessage} = useLivePreviewData({
    draftStep,
    clientStateId,
  });

  if (previewStatus === AsyncStatus.LOADING) {
    return <CenteredHorizontalThrobber />;
  }

  if (previewStatus === AsyncStatus.ERROR) {
    if (statusMessage) {
      const cannotPreviewPattern = /^Step type .+ cannot be previewed\.$/;
      if (cannotPreviewPattern.test(statusMessage)) {
        return (
          <Box mx="md" my="sm">
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="This step type cannot be previewed without saving."
            />
          </Box>
        );
      }
    }
    // Surface the server's user-facing message when available; otherwise show a generic message.
    const errorText = errorUserMessage ?? 'A preview could not be generated.';
    return (
      <Box mx="md" my="sm">
        <InlineBanner bannerType={InlineBannerType.ERROR} text={errorText} />
      </Box>
    );
  }

  return (
    <PresentationalStepDataPreview
      stepType={draftStep.stepType}
      stepVersion={draftStep.stepVersion}
      stepDataRole={StepDataRole.OUTPUT}
      clientStateId={clientStateId}
      preview={previewStepDatas ?? {}}
    />
  );
}
